import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X, ArrowUp, MessageCircle, ShoppingBag } from "lucide-react";
import { img } from "../lib/api";
import { tstyle } from "./textstyle";

export function Tilt({ on, children, className = "", style }) {
  const ref = useRef();
  if (!on) return <div className={className} style={style}>{children}</div>;
  const move = (e) => {
    const el = ref.current, r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-y * 10).toFixed(2)}deg) rotateY(${(x * 12).toFixed(2)}deg) translateY(-6px)`;
    el.style.boxShadow = "0 24px 40px -20px rgba(0,0,0,.4)";
  };
  const leave = () => { ref.current.style.transform = ""; ref.current.style.boxShadow = ""; };
  return <div ref={ref} onMouseMove={move} onMouseLeave={leave} className={`${className} transition-[transform,box-shadow] duration-200 ease-out will-change-transform`} style={style} data-testid="tilt-card">{children}</div>;
}

export function Rail({ children, auto, testid = "rail" }) {
  const ref = useRef();
  const [pause, setPause] = useState(false);
  const go = (d) => {
    const el = ref.current;
    if (!el) return;
    if (d > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) el.scrollTo({ left: 0, behavior: "smooth" });
    else el.scrollBy({ left: d * el.clientWidth * 0.8, behavior: "smooth" });
  };
  useEffect(() => {
    if (!auto || pause) return;
    const t = setInterval(() => go(1), 3500);
    return () => clearInterval(t);
  }, [auto, pause]);
  const arrow = "absolute top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full grid place-items-center bg-white/90 text-slate-900 shadow-lg opacity-0 group-hover/rail:opacity-100 hover:scale-110 transition-[opacity,transform] duration-200";
  return (
    <div className="relative group/rail" onMouseEnter={() => setPause(true)} onMouseLeave={() => setPause(false)} onTouchStart={() => setPause(true)}>
      <div ref={ref} className="flex gap-4 md:gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2" data-testid={testid}>{children}</div>
      <button type="button" onClick={() => go(-1)} className={`${arrow} left-2`} aria-label="Previous" data-testid={`${testid}-prev`}><ChevronLeft className="h-5 w-5" /></button>
      <button type="button" onClick={() => go(1)} className={`${arrow} right-2`} aria-label="Next" data-testid={`${testid}-next`}><ChevronRight className="h-5 w-5" /></button>
    </div>
  );
}

const HIDDEN = { fade: "opacity-0", slide: "opacity-0 translate-y-10", zoom: "opacity-0 scale-95", left: "opacity-0 -translate-x-10" };

export function Reveal({ mode, children }) {
  const ref = useRef();
  const [shown, setShown] = useState(!HIDDEN[mode] || typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (shown || !ref.current) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.05, rootMargin: "0px 0px -40px 0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [shown]);
  if (!HIDDEN[mode]) return children;
  return <div ref={ref} className={`transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none ${shown ? "opacity-100 translate-x-0 translate-y-0 scale-100" : HIDDEN[mode]}`}>{children}</div>;
}

export function Announcement({ ctx }) {
  const a = ctx.theme.header?.announcement;
  if (!a?.enabled || !a.text) return null;
  const st = { background: a.bg, color: a.color, ...tstyle(a.ts, ctx) };
  if (!a.scroll) return <div className="text-center text-xs tracking-wide py-2 px-4" style={st} data-testid="store-announcement">{a.text}</div>;
  const dur = Math.round(80 / Math.max(1, Math.min(10, Number(a.speed) || 4)));
  return (
    <div className="overflow-hidden whitespace-nowrap py-2 text-xs tracking-wide" style={st} data-testid="store-announcement">
      <div className="inline-flex tt-marquee" style={{ animationDuration: `${dur}s`, animationDirection: a.direction === "ltr" ? "reverse" : "normal" }} data-testid="store-announcement-track">
        {Array.from({ length: 8 }, (_, i) => <span key={i} className="px-10">{a.text}</span>)}
      </div>
    </div>
  );
}

const JUSTIFY = { left: "justify-start", center: "justify-center", right: "justify-end" };

export function BrandTile({ ctx, SLink, headingFont }) {
  const h = ctx.theme.header;
  const b = h.brand || {};
  if (b.enabled === false) return null;
  const logo = b.show_logo !== false && h.logo_image;
  const name = b.show_name !== false && h.logo_text;
  if (!logo && !name) return null;
  const ph = ctx.device === "mobile" || (!ctx.device && typeof window !== "undefined" && window.innerWidth < 768);
  const lh = Number(b.logo_h) || 56;
  const pad = b.pad ?? 18;
  return (
    <div style={{ background: b.bg || h.bg, color: b.color || h.color, paddingTop: ph ? Math.round(pad * 0.5) : pad, paddingBottom: ph ? Math.round(pad * 0.5) : pad }} data-testid="store-brand-tile">
      <SLink ctx={ctx} to="/" testid="store-brand-link" className={`max-w-[1320px] mx-auto px-4 md:px-10 flex items-center gap-3 ${JUSTIFY[b.align || "center"]}`}>
        {logo && <img src={img(h.logo_image)} alt={h.logo_text} style={{ height: ph ? Math.round(lh * 0.65) : lh }} className="object-contain max-w-[70vw]" data-testid="store-brand-logo" />}
        {name && <span className="tracking-tight" style={{ fontFamily: `'${headingFont}', serif`, fontSize: ph ? 22 : 34, ...tstyle(b.ts, ctx) }} data-testid="store-brand-name">{h.logo_text}</span>}
      </SLink>
    </div>
  );
}

export function PopupBanner({ ctx, force, onClose }) {
  const p = ctx.theme.popup || {};
  const [open, setOpen] = useState(false);
  const key = `tt_popup_${ctx.slug}`;
  useEffect(() => {
    if (!p.enabled || ctx.noNav || sessionStorage.getItem(key)) return;
    const show = () => setOpen(true);
    if (p.trigger === "exit") {
      const out = (e) => { if (e.clientY <= 0) show(); };
      document.addEventListener("mouseout", out);
      const t = setTimeout(show, 20000);
      return () => { document.removeEventListener("mouseout", out); clearTimeout(t); };
    }
    const t = setTimeout(show, (Number(p.delay) || 5) * 1000);
    return () => clearTimeout(t);
  }, [p.enabled, p.trigger, p.delay, ctx.noNav]); // eslint-disable-line
  if (!(force || open)) return null;
  const close = () => { setOpen(false); if (!ctx.noNav) sessionStorage.setItem(key, "1"); onClose?.(); };
  const s = ctx.theme.settings;
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4 tt-fade-in" onClick={close} data-testid="store-popup">
      <div onClick={(e) => e.stopPropagation()} className={`relative w-full ${p.image ? "max-w-3xl grid md:grid-cols-2" : "max-w-md"} overflow-hidden shadow-2xl tt-pop-in`} style={{ background: p.bg || s.bg, color: p.color || s.text, borderRadius: s.radius }}>
        <button onClick={close} className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-white/90 text-slate-900 grid place-items-center hover:rotate-90 transition-transform" aria-label="Close" data-testid="store-popup-close"><X className="h-4 w-4" /></button>
        {p.image && <img src={img(p.image)} alt="" className="w-full h-48 md:h-full object-cover" />}
        <div className="p-8 md:p-10 text-center flex flex-col justify-center">
          <div className="text-3xl mb-3" style={{ fontFamily: `'${s.heading_font}', serif`, ...tstyle(p.title_ts, ctx) }} data-testid="store-popup-title">{p.title || "Special offer"}</div>
          {p.text && <p className="opacity-80 mb-6 text-sm" style={tstyle(p.text_ts, ctx)}>{p.text}</p>}
          {p.button_text && (ctx.noNav ? <span className="inline-block px-7 py-3 text-sm font-semibold" style={{ background: s.primary, color: s.on_primary, borderRadius: s.radius }}>{p.button_text}</span>
            : <a href={(p.button_link || "/shop").startsWith("http") ? p.button_link : ctx.base + (p.button_link || "/shop")} onClick={close} className="inline-block px-7 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform" style={{ background: s.primary, color: s.on_primary, borderRadius: s.radius }} data-testid="store-popup-btn">{p.button_text}</a>)}
        </div>
      </div>
    </div>
  );
}

export function FloatingButtons({ ctx }) {
  const f = ctx.theme.floating || {};
  const s = ctx.theme.settings;
  const [top, setTop] = useState(false);
  useEffect(() => {
    if (!f.back_to_top || ctx.noNav) return;
    const on = () => setTop(window.scrollY > 600);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [f.back_to_top, ctx.noNav]);
  const wa = String(f.whatsapp || "").replace(/\D/g, "");
  if (!wa && !f.shop_label && !f.back_to_top) return null;
  const side = f.position === "left" ? "left-4 items-start" : "right-4 items-end";
  const btn = "shadow-xl hover:scale-110 hover:-translate-y-1 transition-transform duration-200";
  const href = (u) => (u || "/shop").startsWith("http") ? u : ctx.base + (u || "/shop");
  return (
    <div className={`${ctx.noNav ? "absolute" : "fixed"} bottom-5 z-40 flex flex-col gap-3 ${side}`} data-testid="store-floating">
      {f.back_to_top && (top || ctx.noNav) && <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className={`h-11 w-11 rounded-full grid place-items-center bg-white text-slate-900 ${btn}`} aria-label="Back to top" data-testid="store-floating-top"><ArrowUp className="h-5 w-5" /></button>}
      {f.shop_label && <a href={ctx.noNav ? undefined : href(f.shop_link)} className={`px-5 h-11 rounded-full inline-flex items-center gap-2 text-sm font-semibold tt-float ${btn}`} style={{ background: s.primary, color: s.on_primary }} data-testid="store-floating-shop"><ShoppingBag className="h-4 w-4" />{f.shop_label}</a>}
      {wa && <a href={ctx.noNav ? undefined : `https://wa.me/${wa}${f.whatsapp_text ? `?text=${encodeURIComponent(f.whatsapp_text)}` : ""}`} target="_blank" rel="noreferrer noopener" className={`h-14 w-14 rounded-full grid place-items-center bg-[#25D366] text-white ${btn}`} aria-label="Chat on WhatsApp" data-testid="store-floating-whatsapp"><MessageCircle className="h-7 w-7" /></a>}
    </div>
  );
}
